// api.js - CORRECTED VERSION
import axios from 'axios';

// ✅ Railway-compatible API configuration - FIXED VERSION
// Use REACT_APP_BASE_URL for consistency with working version
const API_BASE_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

console.log('🔗 Admin API Configuration:', {
  API_BASE_URL,
  environment: process.env.NODE_ENV
});

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('adminToken');
      // Don't redirect here, let components handle it
    }
    return Promise.reject(error);
  }
);

// Complete Admin API endpoints - FIXED: All endpoints start with /api/admin/
export const adminAPI = {
  // ====================
  // AUTH ENDPOINTS
  // ====================
  login: (credentials) => api.post('/api/admin/auth/login', credentials),
  getProfile: () => api.get('/api/admin/auth/profile'),
  changePassword: (data) => api.post('/api/admin/auth/change-password', data),
  verifyToken: () => api.get('/api/admin/auth/verify'),

  // ====================
  // DASHBOARD ENDPOINTS
  // ====================
  getDashboardStats: () => api.get('/api/admin/users/dashboard/stats'),
  getRecentActivities: (limit = 10) => 
    api.get(`/api/admin/users/dashboard/activities?limit=${limit}`),

  // ====================
  // USER MANAGEMENT ENDPOINTS
  // ====================
  getUsers: (type, page = 1, limit = 20) => 
    api.get(`/api/admin/users?type=${type}&page=${page}&limit=${limit}`),
  getUserById: (userId) => api.get(`/api/admin/users/${userId}`),
  getPendingTutors: () => api.get('/api/admin/users/tutors/pending'),
  approveTutor: (tutorId) => api.post(`/api/admin/users/tutors/${tutorId}/approve`),
  rejectTutor: (tutorId, reason) => 
    api.post(`/api/admin/users/tutors/${tutorId}/reject`, { reason }),
  
  // 🔧 FIXED: Removed duplicate /users from ban/unban endpoints
  banUser: (userId, reason) => 
    api.post(`/api/admin/users/${userId}/ban`, { reason }),
  unbanUser: (userId) => api.post(`/api/admin/users/${userId}/unban`),

  // NEW: Email endpoint
  sendEmailToUser: (userId, emailData) => 
    api.post(`/api/admin/users/${userId}/send-email`, emailData),

  // ====================
  // FINANCIAL ENDPOINTS
  // ====================
  getFinancialOverview: (timeframe = 'month') => 
    api.get(`/api/admin/financial/financial-overview?timeframe=${timeframe}`),
  getRefundRequests: (status = 'pending', page = 1, limit = 20) => 
    api.get(`/api/admin/financial/refunds?status=${status}&page=${page}&limit=${limit}`),
  processRefund: (refundId, action, adminNotes) => 
    api.post(`/api/admin/financial/refunds/${refundId}/process`, { action, adminNotes }),
  getCommissionReports: (startDate, endDate, page = 1, limit = 20) => 
    api.get(`/api/admin/financial/commissions?startDate=${startDate}&endDate=${endDate}&page=${page}&limit=${limit}`),

  // ====================
  // CONTENT MODERATION ENDPOINTS
  // ====================
  getAssignments: (status = 'open', page = 1, limit = 20) => 
    api.get(`/api/admin/content/assignments?status=${status}&page=${page}&limit=${limit}`),
  moderateAssignment: (assignmentId, action, reason) => 
    api.post(`/api/admin/content/assignments/${assignmentId}/moderate`, { action, reason }),
  getBids: (status = 'pending', page = 1, limit = 20) => 
    api.get(`/api/admin/content/bids?status=${status}&page=${page}&limit=${limit}`),
  moderateBid: (bidId, action, reason) => 
    api.post(`/api/admin/content/bids/${bidId}/moderate`, { action, reason }),
  getMessages: (page = 1, limit = 20) => 
    api.get(`/api/admin/content/messages?page=${page}&limit=${limit}`),
  removeMessage: (messageId, reason) => 
    api.post(`/api/admin/content/messages/${messageId}/remove`, { reason }),

  // ====================
  // SYSTEM MONITORING ENDPOINTS
  // ====================
  getSystemPerformance: () => api.get('/api/admin/system/performance'),
  getSystemLogs: (action, page = 1, limit = 50) => 
    api.get(`/api/admin/system/logs?action=${action}&page=${page}&limit=${limit}`),
  getDisputes: (status = 'pending', page = 1, limit = 20) => 
    api.get(`/api/admin/system/disputes?status=${status}&page=${page}&limit=${limit}`),
  resolveDispute: (disputeId, resolution, adminNotes, refundPercentage) => 
    api.post(`/api/admin/system/disputes/${disputeId}/resolve`, { resolution, adminNotes, refundPercentage }),

  // ====================
  // ANALYTICS ENDPOINTS
  // ====================
  getPlatformGrowth: (period = '7d') => 
    api.get(`/api/admin/analytics/platform-growth?period=${period}`),
  getRevenueAnalytics: (timeframe = 'month') => 
    api.get(`/api/admin/analytics/revenue-analytics?timeframe=${timeframe}`),
  getTutorPerformance: (page = 1, limit = 20) => 
    api.get(`/api/admin/analytics/tutor-performance?page=${page}&limit=${limit}`),
  generateReport: (reportType, startDate, endDate) => 
    api.post('/api/admin/analytics/generate-report', { reportType, startDate, endDate }),

  // ====================
  // ADMIN MANAGEMENT ENDPOINTS (Super Admin Only)
  // ====================
  getAllAdmins: (page = 1, limit = 20) => 
    api.get(`/api/admin/management/admins?page=${page}&limit=${limit}`),
  createAdmin: (adminData) => api.post('/api/admin/management/admins', adminData),
  updateAdmin: (adminId, updateData) => 
    api.put(`/api/admin/management/admins/${adminId}`, updateData),
  deleteAdmin: (adminId) => api.delete(`/api/admin/management/admins/${adminId}`),
  getPermissionTemplates: () => api.get('/api/admin/management/permissions/templates'),
};

export default api;